import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { TextCell } from '../../../../components/table/TextCell';
import { surveyPlansApi } from '../surveyApi';
import type { SurveyPlan } from '../surveyTypes';
import { SurveyPlanCreateModal } from './SurveyPlanCreateModal';
import { SurveyPlanMembers } from './SurveyPlanMembers';
import { useMemberNames } from './useMemberNames';

/**
 * Survey → Development Plan: the PLANS created for this survey's assessed employees, in the
 * standard panel table (product call, 12 Oct 2026 — the rows were the assessed employees).
 *
 * - **Create** opens the two-step form: the plan, then the assessed employees it is for. Each
 *   gets their own copy; it takes `surveys.plan`.
 * - **Title** is the row's anchor and opens the plan's own profile.
 * - **Personnel** is the employees it was created for, as small pictures. Names are resolved in
 *   ONE batch and never stored; above the reader's access level a member reads Restricted and no
 *   picture is fetched.
 *
 * An employee's OWN copy — its approval, sending and training — is reached from the plan's
 * profile, or from Learning & Development › Development Plan.
 */
export function SurveyDevelopmentPlanPanel({ surveyId, onChanged }: { surveyId: string; onChanged?: () => void }) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const fetchPage = useCallback(
    (query: DataTableQuery, signal: AbortSignal) => surveyPlansApi.list(surveyId, query, signal),
    [surveyId],
  );

  const state = useDataTable<SurveyPlan>({ fetchPage, pageSize: 10, defaultSort: 'createdAt:desc' });

  // Every member on the page, for ONE batch resolve.
  const members = useMemo(() => state.rows.flatMap((row) => row.members), [state.rows]);
  const nameOf = useMemberNames(members);

  const columns = useMemo<DataColumn<SurveyPlan>[]>(
    () => [
      { key: 'title', header: 'Title', width: 320, sortable: true, render: (row) => <TextCell value={row.title} /> },
      {
        key: 'members',
        header: 'Personnel',
        width: 260,
        summary: true,
        render: (row) => <SurveyPlanMembers members={row.members} nameOf={nameOf} />,
      },
    ],
    [nameOf],
  );

  return (
    // The tab is `bleed`, so the inset is this wrapper's — as on the other tabs.
    <div className="flex h-full min-w-0 flex-col p-4">
      <DataTablePanel<SurveyPlan>
        id="survey-development-plan"
        state={state}
        columns={columns}
        rowKey={(row) => row.id}
        searchPlaceholder="Search plans"
        fill
        onCreate={() => setCreating(true)}
        createPermission="surveys.plan"
        // The TITLE opens the plan's profile — a page, so the breadcrumb carries the way back.
        onRowOpen={(row) => navigate(`/learning-development/survey/${surveyId}/plan/${row.id}`)}
        openPermission="surveys.read"
        otherInfo={(row) => row}
        emptyMessage="No development plan has been created yet."
        emptySearchMessage="No plan's title matches that search."
      />

      {creating ? (
        <SurveyPlanCreateModal
          open
          surveyId={surveyId}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            state.reload();
            onChanged?.();
          }}
        />
      ) : null}
    </div>
  );
}
